<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { usePolicy } from 'dashboard/composables/usePolicy';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  assistant: {
    type: Object,
    default: () => ({}),
  },
  apiSpec: {
    type: Object,
    default: () => ({}),
  },
  enabled: {
    type: Boolean,
    default: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['action']);
const { checkPermissions } = usePolicy();

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const menuItems = computed(() => {
  const allOptions = [];

  if (checkPermissions(['administrator'])) {
    allOptions.push(
      {
        label: t('CAPTAIN.TOOLS.OPTIONS.EDIT_ACTION'),
        value: 'edit',
        action: 'edit',
        icon: 'i-lucide-pencil',
      },
      {
        label: t('CAPTAIN.TOOLS.OPTIONS.DELETE_ACTION'),
        value: 'delete',
        action: 'delete',
        icon: 'i-lucide-trash',
      }
    );
  }

  return allOptions;
});

const createdAt = computed(() => dynamicTime(props.createdAt));

const methodBadgeClasses = computed(() => {
  const colorClasses = {
    GET: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    PUT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    PATCH:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    DELETE: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  };
  return colorClasses[props.apiSpec.method] || 'bg-n-slate-3 text-n-slate-11';
});

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};
</script>

<template>
  <CardLayout>
    <div class="flex gap-1 justify-between w-full">
      <div class="flex gap-2 items-center flex-1 min-w-0">
        <span class="text-base text-n-slate-12 font-medium line-clamp-1">
          {{ title }}
        </span>
        <span
          :class="methodBadgeClasses"
          class="px-2 py-0.5 rounded text-xs font-medium shrink-0"
        >
          {{ apiSpec.method }}
        </span>
        <span
          v-if="!enabled"
          class="px-2 py-0.5 rounded text-xs font-medium shrink-0 bg-n-slate-3 text-n-slate-11"
        >
          {{ $t('CAPTAIN.TOOLS.DISABLED') }}
        </span>
      </div>
      <div class="flex gap-2 items-center">
        <div
          v-on-clickaway="() => toggleDropdown(false)"
          class="flex relative items-center group"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="top-full mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0"
            @action="handleAction($event)"
          />
        </div>
      </div>
    </div>
    <p class="text-sm text-n-slate-11 line-clamp-2 mb-2">
      {{ description }}
    </p>
    <div class="flex gap-4 justify-between items-center w-full">
      <span
        class="flex gap-1 items-center text-sm truncate shrink-0 text-n-slate-11"
      >
        <i class="i-lucide-life-buoy" />
        {{ assistant?.name || '' }}
      </span>
      <span
        class="flex flex-1 gap-1 justify-start items-center text-sm truncate text-n-slate-11"
      >
        <i class="i-lucide-code shrink-0" />
        <span class="truncate font-mono text-xs">{{ name }}</span>
      </span>
      <div class="text-sm shrink-0 text-n-slate-11 line-clamp-1">
        {{ createdAt }}
      </div>
    </div>
  </CardLayout>
</template>
